import { useTheme } from "next-themes";
import { Toaster as Sonner, toast } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          /* A failure has to LOOK like one. The Radix toast this replaced gave
             `variant="destructive"` a solid destructive card, and sonner's
             classNames had no per-type entry — so every `toast.error` rendered
             byte-identical to a success. 22 error toasts, all of them silent
             about being errors. `error` mirrors what the deleted component
             did; `success` stays neutral because Radix's default variant was
             neutral too, and a wall of green is not an improvement. */
          error:
            "group-[.toaster]:border-destructive group-[.toaster]:bg-destructive group-[.toaster]:text-destructive-foreground " +
            /* The description carries `text-muted-foreground` from the rule
               above, which on a destructive card is muted-on-red and barely
               legible — so it is overridden here, on the error card only. */
            "group-[.toaster]:[&_[data-description]]:text-destructive-foreground/90",
          /* Radius comes from the token chain like everything else. This was
             `rounded-none`, justified in a comment as "zero radius is the
             brand" — that brand position was retired on 2026-10-16 with the
             move to canon, and DESIGN.md now records no departures at all. */
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast };
